/**
 * Gestor de configuraciones específicas por tipo de reporte
 */
class ReportConfigManager {
    constructor() {
        this.configs = {
            recorridos: {
                modalTitle: 'Reporte de Recorridos',
                defaultTitle: () => `Reporte de Recorridos - ${new Date().toLocaleDateString()}`,
                chartsLabel: 'Gráficos (Mapa, Velocidad, Kilometraje, Visitas)',
                tableLabel: 'Tabla de recorridos detallada',
                recommendedOrientation: 'portrait',
                shareText: (date, size) => `🚗 *Reporte de Recorridos - Quro GPS*\n\n📅 Generado: ${date}\n📊 Tamaño: ${size} KB\n🗺️ Incluye: Mapa, velocidades, kilometraje y tabla detallada\n📈 Análisis completo de rutas y estadías\n\n🔗 Sistema Quro GPS\n📎 Archivo PDF disponible para descarga`,
                tableTitle: 'Listado Detallado de Recorridos',
                fallbackTitles: [
                    'Mapa de Recorridos',
                    'Análisis de Velocidad',
                    'Análisis de Kilometraje',
                    'Visitas Frecuentes'
                ]
            },
            alertas: {
                modalTitle: 'Reporte de Alertas',
                defaultTitle: () => `Reporte de Alertas - ${new Date().toLocaleDateString()}`,
                chartsLabel: 'Gráficos y análisis visuales',
                tableLabel: 'Tabla de alertas detallada',
                recommendedOrientation: 'portrait',
                shareText: (date, size) => `🚨 *Reporte de Alertas - Quro GPS*\n\n📅 Generado: ${date}\n📊 Tamaño: ${size} KB\n🔔 Incluye: Análisis de alertas, gráficos y tabla detallada\n📈 Monitoreo completo de eventos\n\n🔗 Sistema Quro GPS\n📎 Archivo PDF disponible para descarga`,
                tableTitle: 'Listado Detallado de Alertas',
                fallbackTitles: [
                    'Análisis General',
                    'Alertas Detonadas',
                    'Ubicaciones de Alertas',
                    'Análisis de Frecuencia'
                ]
            },
            combustible: {
                modalTitle: 'Reporte de Combustible',
                defaultTitle: () => `Reporte de Combustible - ${new Date().toLocaleDateString()}`,
                chartsLabel: 'Gráficos de consumo y niveles',
                tableLabel: 'Tabla de datos de combustible',
                recommendedOrientation: 'portrait',
                shareText: (date, size) => `⛽ *Reporte de Combustible - Quro GPS*\n\n📅 Generado: ${date}\n📊 Tamaño: ${size} KB\n📈 Incluye: Análisis de consumo, niveles y tendencias\n⚡ Control completo de combustible\n\n🔗 Sistema Quro GPS\n📎 Archivo PDF disponible para descarga`,
                tableTitle: 'Listado de Datos de Combustible',
                fallbackTitles: [
                    'Análisis de Consumo',
                    'Niveles de Combustible',
                    'Tendencias',
                    'Resumen de Combustible'
                ]
            },
            generico: {
                modalTitle: 'Reporte del Sistema',
                defaultTitle: () => `Reporte del Sistema - ${new Date().toLocaleDateString()}`,
                chartsLabel: 'Gráficos y componentes visuales',
                tableLabel: 'Tabla de datos detallada',
                recommendedOrientation: 'landscape',
                shareText: (date, size) => `📋 *Reporte del Sistema - Quro GPS*\n\n📅 Generado: ${date}\n📊 Tamaño: ${size} KB\n📈 Incluye: Gráficos, tablas y análisis completo\n\n🔗 Sistema Quro GPS\n📎 Archivo PDF disponible para descarga`,
                tableTitle: 'Listado de Datos',
                fallbackTitles: [
                    'Análisis Principal',
                    'Componentes Visuales',
                    'Información Adicional',
                    'Resumen General'
                ]
            },
            estadias: {
                modalTitle: 'Reporte de Estadías',
                defaultTitle: () => `Reporte de Estadías - ${new Date().toLocaleDateString()}`,
                chartsLabel: 'Gráficos y métricas de estadías',
                tableLabel: 'Tabla de estadías detallada',
                recommendedOrientation: 'landscape',
                shareText: (date, size) => `🅿️ *Reporte de Estadías - Quro GPS*\n\n📅 Generado: ${date}\n📊 Tamaño: ${size} KB\n📈 Incluye: Estadías por vehículo, mapas y tabla detallada\n\n🔗 Sistema Quro GPS\n📎 Archivo PDF disponible para descarga`,
                tableTitle: 'Listado de Estadías',
                fallbackTitles: [
                    'Listado Estadias',
                    'Reporte de Estadías',
                    'Estadías por vehículo'
                ]
            },
            universal: {
                modalTitle: 'Reporte del Sistema',
                defaultTitle: () => `Reporte del Sistema - ${new Date().toLocaleDateString()}`,
                chartsLabel: 'Componentes visuales',
                tableLabel: 'Tabla de datos',
                recommendedOrientation: 'landscape',
                useUniversalCapture: true
            }
        };
    }

    detectReportType(options = {}) {
        // Override manual
        if (options.reportType) {
            return options.reportType || 'universal';
        }

        // Detectar por URL
        const currentPath = window.location.pathname.toLowerCase();
        if (currentPath.includes('estadia') || currentPath.includes('estadias')) return 'estadias';
        if (currentPath.includes('recorridos') || currentPath.includes('recorrido')) return 'recorridos';
        if (currentPath.includes('alertas') || currentPath.includes('alerta')) return 'alertas';
        if (currentPath.includes('combustible')) return 'combustible';
        if (currentPath.includes('velocidad')) return 'velocidad';
        if (currentPath.includes('ubicaciones') || currentPath.includes('ubicacion')) return 'ubicaciones';

        // Detectar por título de página
        const pageTitle = document.title.toLowerCase();
        if (pageTitle.includes('estadia')) return 'estadias';
        if (pageTitle.includes('recorrido')) return 'recorridos';
        if (pageTitle.includes('alerta')) return 'alertas';
        if (pageTitle.includes('combustible')) return 'combustible';

        // Detectar por encabezados
        const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
        for (const heading of headings) {
            const text = heading.textContent.toLowerCase();
            if (text.includes('estadia')) return 'estadias';
            if (text.includes('recorrido')) return 'recorridos';
            if (text.includes('alerta')) return 'alertas';
            if (text.includes('combustible')) return 'combustible';
        }

        return 'generico';
    }

    shouldUseUniversalCapture(reportType) {
        const config = this.reportConfigs[reportType];
        return config?.useUniversalCapture === true || reportType === 'universal';
    }

    // ✅ NUEVO: Obtener estrategia de captura
    getCaptureStrategy(reportType) {
        if (reportType === 'alertas') {
            return 'specific-alertas'; // Mantener lógica específica
        }
        return 'universal'; // Para todos los demás
    }


    getConfig(reportType = null) {
        if (!reportType) {
            reportType = this.detectReportType();
        }
        return this.configs[reportType] || this.configs['generico'];
    }

    getModalTitle(reportType = null) {
        return this.getConfig(reportType).modalTitle;
    }

    getDefaultTitle(reportType = null) {
        return this.getConfig(reportType).defaultTitle();
    }

    getChartsLabel(reportType = null) {
        return this.getConfig(reportType).chartsLabel;
    }

    getTableLabel(reportType = null) {
        return this.getConfig(reportType).tableLabel;
    }

    getShareText(reportType = null, date, size) {
        return this.getConfig(reportType).shareText(date, size);
    }

    isRecommendedLandscape(reportType = null) {
        return this.getConfig(reportType).recommendedOrientation === 'landscape';
    }

    isRecommendedPortrait(reportType = null) {
        return this.getConfig(reportType).recommendedOrientation === 'portrait';
    }

    getFallbackTitles(reportType = null) {
        return this.getConfig(reportType).fallbackTitles;
    }
}

export default ReportConfigManager;